/* eslint-disable import/prefer-default-export */
import isEmptyUtil from '@utils/isEmpty';
import {
  getDateDifference,
  getRegularDate,
  getRelativeDayOrCurrentDateWithTDays,
  todayTomorrowConfig
} from '@utils/dates';
import getItemFromSessionStorage from '@utils/getItemFromSessionStorage';
import { OPTIONS_MAP } from './constants';

const avaliableTestOptions = {
  [OPTIONS_MAP.ALL]: true,
  [OPTIONS_MAP.UPCOMING]: true,
  [OPTIONS_MAP.ONGOING]: true
};

export const getValidTestType = (type) => {
  const parsedTestType = parseInt(type, 10);

  if (isEmptyUtil(parsedTestType)) {
    return OPTIONS_MAP.ONGOING;
  }

  if (avaliableTestOptions[parsedTestType]) {
    return parsedTestType;
  }

  return OPTIONS_MAP.ONGOING;
};

export const getLanguageString = (languages) => {
  return languages.reduce((acc, curr, index) => {
    let result = '';
    if (index === 0) {
      result = `${curr.displayName}`;
    } else if (index === languages.length - 1) {
      result = `${acc} & ${curr.displayName}`;
    } else {
      result = `${acc}, ${curr.displayName}`;
    }
    return result;
  }, '');
};

export const getEducatorsString = (educators) => {
  return educators.reduce((acc, curr, index) => {
    let result = '';
    if (index === 0) {
      result = `${curr.firstName} ${curr.lastName}`;
    } else if (index === educators.length - 1) {
      result = `${acc} & ${curr.firstName} ${curr.lastName}`;
    } else {
      result = `${acc}, ${curr.firstName} ${curr.lastName}`;
    }
    return result;
  }, '');
};

export const getDateStringInTestSeriesHeader = (startsAt, endsAt) => {
  const startsAtDate = new Date(startsAt);
  const endsAtDate = endsAt ? new Date(endsAt) : null;

  const startsOn = getRegularDate(startsAtDate);
  const currentDate = new Date();
  let dateString = '';
  if (currentDate.getTime() < startsAtDate.getTime()) {
    dateString = `Test starts ${startsOn}`;
  } else if (currentDate.getTime() > startsAtDate.getTime() && !endsAtDate) {
    dateString = `Started on ${startsOn}`;
  } else if (
    currentDate.getTime() > startsAtDate.getTime() &&
    currentDate.getTime() < endsAtDate.getTime()
  ) {
    dateString = `Started on ${startsOn}`;
  } else {
    dateString = `Ended on ${getRegularDate(endsAtDate)}`;
  }

  return dateString;
};

export const isPastTest = (endsAt = '') => {
  const endsAtDate = endsAt ? new Date(endsAt) : null;
  const currentDate = new Date();

  if (currentDate.getTime() > endsAtDate?.getTime()) {
    return true;
  }
  return false;
};

export const getTestSeriesDetailsAPIKey = (uid) => {
  return `test-series-svc/collection/v1/${uid}/details/`;
};

export const getSkuDetailsAPIKey = (uid, goalUid) => {
  return `v1/subscriptions/user/store_skus/?content_type_id=501&object_uid=${uid}&goal_uid=${goalUid}&send_extra_details=True&centre_sub_required=True`;
};

export const getEnrollAPIKey = (testSeriesUID, goalUID) => {
  return `test-series-svc/collection/v1/${testSeriesUID}/enroll-state/?goal_uid=${goalUID}`;
};

export const getEnrollmentMessage = (date) => {
  if (!date) return 'N/A';
  const relativeDate = getRelativeDayOrCurrentDateWithTDays(
    date,
    true,
    7,
    true
  );
  const isRelativeDateTodayTomorrow = todayTomorrowConfig.some(
    (day) => day === relativeDate
  );
  if (!relativeDate) {
    return 'Enrolments have closed for this test series';
  }
  if (isRelativeDateTodayTomorrow) {
    return `Enrolment ends ${relativeDate}`;
  }
  if (relativeDate <= 7 && relativeDate > 0) {
    return `Enrolment ends in ${relativeDate} days`;
  }
  return `Enrolment ends on ${relativeDate}`;
};

export const getOfferMessage = (date) => {
  if (!date) return 'N/A';
  const relativeDate = getRelativeDayOrCurrentDateWithTDays(
    date,
    true,
    7,
    true
  );
  const isRelativeDateTodayTomorrow = todayTomorrowConfig.some(
    (day) => day === relativeDate
  );
  if (!relativeDate) {
    return 'Offer is over for this test series';
  }
  if (isRelativeDateTodayTomorrow) {
    return `Offer ends ${relativeDate}`;
  }
  if (relativeDate <= 7 && relativeDate > 0) {
    return `Offer ends in ${relativeDate} days`;
  }
  return `Offer ends on ${relativeDate}`;
};

export const getSkuBasedEventProps = (activeSku, testSeries) => {
  return {
    entity_content_type: 'Test Series',
    entity_title: testSeries?.name,
    entity_uid: testSeries?.uid,
    last_primary_source: 'Store',
    subscription_type: 'Store Subscription',
    offer_price: activeSku?.price,
    offer_end_date: activeSku?.priceEffectiveTill ?? 'NA',
    subscription_price: activeSku?.maxRetailPrice || activeSku?.price,
    subscription_duration: getDateDifference(activeSku?.accessEndDate)
  };
};

const STORE_TEST_SERIES_REFERRAL_CODE_KEY = 'storeTestSeriesReferralCodes';

export const getTestSeriesReferralInStorage = (uid) => {
  try {
    const referralCodesStr = getItemFromSessionStorage(
      STORE_TEST_SERIES_REFERRAL_CODE_KEY
    );

    const referralCodes = !!referralCodesStr && JSON.parse(referralCodesStr);

    return referralCodes && referralCodes[uid];
  } catch {
    return null;
  }
};

export const setTestSeriesReferralInStorage = (uid, code) => {
  try {
    const referralCodesStr = getItemFromSessionStorage(
      STORE_TEST_SERIES_REFERRAL_CODE_KEY
    );

    const referralCodes = !!referralCodesStr && JSON.parse(referralCodesStr);
    sessionStorage.setItem(
      STORE_TEST_SERIES_REFERRAL_CODE_KEY,
      JSON.stringify({ ...referralCodes, [uid]: code })
    );
  } catch {
    return null;
  }
};

export const getFirstLetterForIcon = (text = '') => {
  const trimmedText = text.trim();
  return trimmedText.charAt(0) || 'A';
};
